import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import {
  QueryVal, addQuery, addQueryWithReplace, removeQuery,
} from 'Helpers/Query';
import fr from 'facet-rest';
import useCollection from 'Hooks/useCollection';
import MetaTable from 'Components/UI/Meta/MetaTable';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import useUserContext from 'Context/UserContext';
import { getMeetingSummaryFilters } from 'Resources/MeetingSummary';
import { Skeleton } from '@material-ui/lab';
import { useMeetingContext } from 'Context/MeetingContext';
import { useRefreshMeetingSummary } from 'Hooks/useRefreshEvent';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';

const styles = theme => ({
  root: {
    padding: theme.spacing(3),
    width: '100%',
  },
  loading: {
    padding: theme.spacing(3),
    width: '100%',
    display: 'flex',
  },
  loadingFilter: {
    flex: '1 0 0',
    marginRight: theme.spacing(2),
  },
  skeletonFilter: {
    height: '100%',
    width: '100%',
    transformOrigin: 'top',
    marginTop: theme.spacing(2),
  },
  loadingTable: {
    flex: '4 0 0',
  },
});

function MeetingSummaryTable({ className, classes }) {
  const { collection, setCollection } = useCollection();
  const { agentResource } = useUserContext();
  const { setMeetingResource } = useMeetingContext();
  const [loading, setLoading] = useState(false);
  const relationship = agentResource?.relationship('meetingSummary');
  const { logEvent } = useEventTrackingLogEvent({ nodeName: 'Meeting Summary Table', isPageView: false });

  const queryMeetingSummaryFilterLink = 'meetingSummaryFilterLink';
  const queryMeetingSummaryLink = 'meetingSummaryLink';
  const queryHouseholdId = 'householdId';

  async function loadMeetingSummarys() {
    try {
      //  Get our filter from the URL.
      const filterLink = QueryVal(queryMeetingSummaryFilterLink) || relationship
        .link('related')
        .url();
      setLoading(true);
      const response = await fr.http.Get(filterLink);
      setLoading(false);
      const filteredMeetingSummaryCollection = fr.collection(response);
      setCollection(filteredMeetingSummaryCollection);
      addQuery({ [queryMeetingSummaryFilterLink]: filterLink });

      //  Get any resource link from the URL.
      const resourceLink = QueryVal('meetingSummaryLink');
      if (resourceLink) {
        setLoading(true);
        const resourceLinkResponse = await fr.http.Get(resourceLink);
        setLoading(false);
        const meetingSummaryCollection = fr.collection(resourceLinkResponse);
        const meetingSummaryResource = meetingSummaryCollection.resources().head();
        // eslint-disable-next-line no-unused-expressions
        meetingSummaryResource?.relationship('meeting').link('related').get()
          .then(col => {
            setMeetingResource(col.resources().head());
          })
          .catch((err) => {
            displayErrorMessage(err, err.errors);
          });
      }
    } catch (err) {
      displayErrorMessage(err, err.errors);
      setLoading(false);
    }
  }

  // If the relationship doesn't exist on the household resource, this will need to be updated
  useEffect(() => {
    if (relationship) {
      loadMeetingSummarys();
    }
  }, [agentResource?.id()]);

  useRefreshMeetingSummary(() => {
    loadMeetingSummarys();
  }, [agentResource?.id()]);

  if (collection == null || loading) {
    return (
      <div className={classes.loading}>
        <div className={classes.loadingFilter}>
          <Skeleton animation="wave" className={classes.skeletonFilter} />
        </div>
        <div className={classes.loadingTable}>
          {Array(10).fill(<Skeleton animation="wave" height={64} />)}
        </div>
      </div>
    );
  }

  if (collection == null) {
    return null;
  }

  return (
    <MetaTable
      className={classNames(classes.root, className)}
      collection={collection}
      hasMetaQueryForm
      hasSortableColumns
      QueryFormProps={{
        square: true,
        getAvailableFilters: getMeetingSummaryFilters,
        onReset: () => {
          removeQuery(queryMeetingSummaryFilterLink, queryMeetingSummaryLink, queryHouseholdId);
        },
      }}
      tableMetaOverrideSetName="MeetingSummaryTableOverride"
      linkQueryParamName={queryMeetingSummaryFilterLink}
      features={{
        onClick: (resource) => {
          logEvent('Clicked Mission Summary Item', {});
          addQuery({ [queryHouseholdId]: resource.attr('householdId') });
          addQueryWithReplace({ [queryMeetingSummaryLink]: resource.link('self').url() });
          resource.relationship('meeting').link('related').get()
            .then(col => {
              setMeetingResource(col.resources().head());
            })
            .catch((err) => {
              displayErrorMessage(err, err.errors);
            });
        },
      }}
    />
  );
}

MeetingSummaryTable.defaultProps = {
  className: '',
};

MeetingSummaryTable.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(MeetingSummaryTable);
